/// <reference types="@types/google.maps" />
import { Loader } from '@googlemaps/js-api-loader';
import { Build, Component, Element, Host, Prop, State, h } from '@stencil/core';

import { endpoints } from '../../../.conf/resources';
import { getAssetSrc } from '../../helpers/helpers';
import type {
  MarkerOptions,
  StoreMap,
} from '../../helpers/utils/googlemaps-types';

@Component({
  tag: 'cms-store-map',
  styleUrl: 'cms-store-map.scss',
  shadow: true,
})
export class CmsStoreMap {
  private mapNode: HTMLElement;
  private searchStoreNode: HTMLInputElement;
  private searchStorContainer: HTMLElement;
  private readonly mapsApiKey = endpoints.mapsApiKey;
  private readonly searchResultZoom = 12;
  private readonly iconHbLocator = 'icon-hb-locator.svg';
  private readonly iconBhLocator = 'icon-bh-locator.svg';
  private currentStoreId: number;
  private storesData: StoreMap[];
  private infoWindow = null;
  private readonly mapOptions = {
    center: {
      lat: 0,
      lng: 0,
    },
    zoom: 12,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeId: 'roadmap',
    scrollwheel: true,
    draggable: true,
    mapId: 'DEMO_MAP_ID',
  };
  private autocomplete?: google.maps.places.Autocomplete;
  private googleMap?: google.maps.Map;
  private initialized = false;

  @State() isAutocomplete = false;

  /**
   * set the usercentrics consent status
   */
  @Prop({ reflect: true }) isConsent: boolean;

  /**
   * set the placeholder of the input element
   */
  @Prop() placeholder: string;

  /**
   * set the current selected store latitude like 49.215813
   */
  @Prop() defaultStoreLat: number;

  /**
   * set the current selected store longitude like 8.176083
   */
  @Prop() defaultStoreLong: number;

  /**
   * set all Market Information from alle-mearkte.json
   */
  @Prop() storeDataUrl: string;

  /**
   * set translation for link label like Marktdetails
   */
  @Prop() detailsMarkerTranslation: string;

  /**
   * set translation for link label like Marktdetails
   */
  @Prop() translationGoToGoogleMaps: string;

  /**
   * set translation for link Als meinen Markt festlegen
   */
  @Prop() setStoreMarkerTranslation: string;

  /**
   * set the map region like de
   */
  @Prop() region = 'de';

  /**
   * set the zoom level of the current selected store
   */
  @Prop() customStoreZoom: number;

  /**
   * set the element headline like Finde Deinen HORNBACH Baumarkt
   */
  @Prop() mapHeadline: string;

  @Element() host: HTMLElement;

  private async loadGoogleMaps() {
    if (this.googleMap === undefined) {
      const loader = new Loader({
        apiKey: this.mapsApiKey,
        version: 'weekly',
        libraries: ['maps', 'places', 'geometry', 'marker'],
        region: this.region,
      });

      loader
        .importLibrary('maps')
        .then(({ Map: GoogleMap }) => {
          this.googleMap = new GoogleMap(this.mapNode, this.mapOptions);
          this.googleMap.addListener('idle', () => {
            if (this.initialized) return;
            this.waitForMapComponents();
            this.mapStores(this.storesData);
            this.searchAutocomplete();
            this.initialized = true;
          });
        })
        .catch((error) => {
          console.error(error);
          throw error;
        });
    }
  }

  private waitForMapComponents() {
    /* CMS-2724: AdvancedMarkerElement of the used google-maps isn't available sometimes - even when used within the onfulfilled-promise. 
    Therefore we manually check for it */
    const maxTries = 30;
    let tries = 0;
    while (
      tries < maxTries &&
      (!window.google?.maps?.marker?.AdvancedMarkerElement ||
        !google?.maps?.InfoWindow ||
        !google?.maps?.places?.Autocomplete ||
        !google?.maps?.ControlPosition)
    ) {
      tries++;
      setTimeout(() => console.trace('Waiting for AdvancedMarkerElement'), 125);
    }
    if (tries >= maxTries) {
      console.error('AdvancedMarkerElement is not available after 30 tries.');
    }
  }

  private async mapStores(stores: Array<StoreMap>) {
    return stores.map(async (store: StoreMap) => {
      const { latitude, longitude } = store;
      const markerOptions = {
        position: {
          lat: latitude,
          lng: longitude,
        },
        icon: await this.getMarkerIconSrc(store),
        name: store.name,
        id: store.id,
        street: store.street,
        postalcode: store.postalcode,
        city: store.city,
        url: store.url,
        client: store.client,
      };

      return await this.addMarkerToMap(this.googleMap, markerOptions);
    });
  }

  private async getMarkerIconSrc(store: StoreMap) {
    const { client } = store;
    return client === 'bodenhaus'
      ? getAssetSrc(this.iconBhLocator)
      : getAssetSrc(this.iconHbLocator);
  }

  private async addMarkerToMap(
    map: google.maps.Map,
    markerOptions: MarkerOptions,
  ) {
    const markerImg = document.createElement('img');
    markerImg.src = markerOptions.icon;

    const newMarker = new google.maps.marker.AdvancedMarkerElement({
      position: markerOptions.position,
      content: markerImg,
      map,
    });

    await this.addInfoWindow(newMarker, markerOptions);
  }

  private async infoWindowContent(markerOptions: MarkerOptions) {
    const { name, street, postalcode, city, url, id, client } = markerOptions;
    const mapString =
      `https://maps.google.de/maps?q=${markerOptions.name}+${markerOptions.street}&z19`.replaceAll(
        ' ',
        '+',
      );

    return client !== 'bodenhaus' &&
      this.currentStoreId !== Number.parseFloat(id)
      ? ` 
      <div class="info-window.content-wrapper">
        <div class="store-details-wrapper">  
          <div class="details store-name">${name}</div>
          <div class="details">${street}</div>
          <span class="details">${postalcode}</span> 
          <span class="details">${city}</span>
        </div>
        <div class="details-link-wrapper">
          <a class="details-link" target="_blank" href="${mapString}">${this.translationGoToGoogleMaps}</a>
          <a class="details-link" target="_blank" href="${url}">${this.detailsMarkerTranslation}</a>
        </div>
  
        <cms-button onclick="document.body.dispatchEvent(new CustomEvent('mymarket:changestoreconfirmation:open',
        {detail: {name:'${name}',code:'${id}'}}))" color="brand-1" size="button-sm" type="button"
            label="${this.setStoreMarkerTranslation}">
          </cms-button>
        </div>
        `
      : `       <div class="info-window.content-wrapper">
        <div class="store-details-wrapper">  
          <div class="details">${name}</div>
          <div class="details">${street}</div>
          <span class="details">${postalcode}</span> 
          <span class="details">${city}</span>
        </div>
        <div class="details-link-wrapper">
          <a class="details-link" target="_blank" href="${mapString}">${this.translationGoToGoogleMaps}</a>
          <a class="details-link" target="_blank" href="${url}">${this.detailsMarkerTranslation}</a>
        </div>
        </div>
                `;
  }

  private async addInfoWindow(
    marker: google.maps.marker.AdvancedMarkerElement,
    markerOptions: MarkerOptions,
  ) {
    marker.addListener('click', async () => {
      if (this.infoWindow) {
        await this.infoWindow.close();
      }
      this.infoWindow = new google.maps.InfoWindow({
        content: await this.infoWindowContent(markerOptions),
      });
      await this.infoWindow.open({
        anchor: marker,
        map: this.googleMap,
      });
    });
  }

  private readonly searchInputChanged = (event: Event) => {
    const eventTarget = event.target as HTMLInputElement;
    const inputValue = eventTarget?.value;
    if (inputValue.length > 0) {
      this.isAutocomplete = true;
      return;
    }
    this.resetMap();
    this.isAutocomplete = false;
  };

  private async clearInput() {
    const inputValue = this.searchStoreNode?.value;
    if (inputValue.length <= 0) {
      return;
    }
    this.searchStoreNode.value = '';
    this.resetMap();
    this.isAutocomplete = false;
  }

  private async resetMap() {
    if (this.googleMap !== undefined) {
      this.googleMap?.setZoom(this.customStoreZoom);
      this.googleMap?.panTo(this.mapOptions.center);
    }
  }

  private async searchAutocomplete() {
    const locationOptions = {
      types: ['(regions)'],
      componentRestrictions: { country: [this.region] },
      fields: ['geometry.location'],
    };

    this.autocomplete = new google.maps.places.Autocomplete(
      this.searchStoreNode,
      locationOptions,
    );

    this.googleMap?.controls[google.maps.ControlPosition.TOP_LEFT].push(
      this.searchStorContainer,
    );

    this.autocomplete.addListener('place_changed', async () => {
      const searchResultPlace = this.autocomplete.getPlace();
      if (!searchResultPlace.geometry) {
        return;
      }
      const resultLocation = searchResultPlace.geometry?.location;
      if (this.googleMap !== undefined) {
        this.googleMap.setZoom(this.searchResultZoom);
        this.googleMap.panTo(resultLocation);
      }
    });
  }

  private async fetchData(apiUrl: RequestInfo | URL) {
    if (apiUrl) {
      try {
        const dataResponse = await fetch(apiUrl);
        const data = await dataResponse.json();
        return data.stores;
      } catch (error) {
        console.error('error', error);
        throw error;
      }
    }
  }

  private async setCurrentStoreID() {
    //@ts-ignore
    const hbDataLayer = window?.hbDataLayer;
    if (hbDataLayer) {
      this.currentStoreId = await hbDataLayer['site.market'];
    }
  }

  async componentWillLoad() {
    if (
      this.defaultStoreLat !== undefined &&
      this.defaultStoreLong !== undefined
    ) {
      this.mapOptions.center.lat = this.defaultStoreLat;
      this.mapOptions.center.lng = this.defaultStoreLong;
    }
    if (this.customStoreZoom !== undefined) {
      this.mapOptions.zoom = this.customStoreZoom;
    }

    await this.setCurrentStoreID();
    if (this.storeDataUrl !== undefined) {
      this.storesData = await this.fetchData(this.storeDataUrl);
    }
  }

  async componentDidLoad() {
    this.mapNode = this.host.shadowRoot.querySelector('.store-map');
    this.searchStoreNode = this.host.shadowRoot.querySelector(
      '.search-store',
    ) as HTMLInputElement;

    this.searchStorContainer = this.host.shadowRoot.querySelector(
      '.search-store-container',
    );

    if (this.isConsent && Build.isBrowser) {
      await this.loadGoogleMaps();
    }

    if (this.mapNode !== undefined && this.isConsent && Build.isBrowser) {
      await this.loadGoogleMaps();
    }
  }

  async componentShouldUpdate(
    newValue: boolean,
    _oldValue: boolean,
    name: string,
  ) {
    if (Build.isBrowser && name === 'isConsent') {
      if (newValue === true) {
        await this.loadGoogleMaps();
      } else if (window?.google?.maps && this.googleMap) {
        this.googleMap = undefined;
      }
    }
  }

  render() {
    return (
      <Host>
        <div class={'store-map-container'}>
          <div
            style={
              this.isConsent
                ? { visibility: 'visible' }
                : { visibility: 'hidden' }
            }
            class={'store-map'}
          />
          <div style={{ display: 'none' }}>
            <div class={'search-store-container'}>
              <div
                class={'searchIconWrapper'}
                onClick={() => {
                  this.clearInput();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    this.clearInput();
                  }
                }}
              >
                {this.isAutocomplete ? (
                  <cms-icon size='md' icon='notificationError' />
                ) : (
                  <cms-icon
                    style={{
                      '--cms-theme-svg-color':
                        'var(--cms-semanticColors-base-strong-icon)',
                    }}
                    size='md'
                    icon='search'
                  />
                )}
              </div>
              <input
                type='text'
                class={'search-store'}
                onInput={this.searchInputChanged}
                placeholder={this.placeholder}
              />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
